/*
Copyright 2022-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type MatrixClient } from "matrix-js-sdk";
import {
  KnownMembership,
  type Membership,
} from "matrix-js-sdk/lib/@types/membership";
import { RoomEvent, type Room } from "matrix-js-sdk/lib/models/room";

import { roomAliasLocalpartFromRoomName } from "./matrix";

const waitForRoomAdmission = (
  client: MatrixClient,
  roomId: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    let finished = false;
    let joinInProgress = false;

    function cleanup(): void {
      client.off(RoomEvent.MyMembership, onMyMembership);
    }

    function succeed(): void {
      if (finished) return;
      finished = true;
      cleanup();
      resolve();
    }

    function fail(error: unknown): void {
      if (finished) return;
      finished = true;
      cleanup();
      reject(error);
    }

    function joinRoom(): void {
      if (finished || joinInProgress) return;
      joinInProgress = true;
      void client.joinRoom(roomId).then(succeed, fail);
    }

    function onMyMembership(
      room: Room,
      membership: Membership,
      previousMembership?: Membership,
    ): void {
      if (room.roomId !== roomId || finished) return;

      if (membership === KnownMembership.Invite) {
        joinRoom();
      } else if (membership === KnownMembership.Join) {
        succeed();
      } else if (
        membership === KnownMembership.Ban ||
        (membership === KnownMembership.Leave &&
          previousMembership === KnownMembership.Knock)
      ) {
        fail(new Error("The room administrator rejected the join request"));
      }
    }

    client.on(RoomEvent.MyMembership, onMyMembership);

    const room = client.getRoom(roomId);
    const membership = room?.getMyMembership();

    if (membership === KnownMembership.Join) {
      succeed();
    } else if (membership === KnownMembership.Invite) {
      joinRoom();
    } else if (membership === KnownMembership.Ban) {
      fail(new Error("The guest account is banned from this room"));
    } else {
      void client.knockRoom(roomId).then(({ room_id: knockedRoomId }) => {
        if (knockedRoomId !== roomId) {
          fail(new Error("The homeserver returned an unexpected room ID"));
          return;
        }

        const updatedRoom = client.getRoom(roomId);
        if (updatedRoom) {
          onMyMembership(updatedRoom, updatedRoom.getMyMembership());
        }
      }, fail);
    }
  });

export const joinGuestRoomByName = async (
  client: MatrixClient,
  roomName: string,
): Promise<string> => {
  const domain = client.getDomain();
  if (!domain) throw new Error("The guest homeserver domain is unavailable");

  const roomAlias = `#${roomAliasLocalpartFromRoomName(roomName)}:${domain}`;

  let roomId: string;
  try {
    const result = await client.getRoomIdForAlias(roomAlias);
    roomId = result.room_id;
  } catch (error) {
    throw new Error(
      `Could not find the room "${roomName}". Expected room alias: ${roomAlias}`,
      { cause: error },
    );
  }

  await waitForRoomAdmission(client, roomId);
  return roomId;
};
